<div class="m-container p-4">
    <!-- Loading State -->
    <div *ngIf="isChecking" class="d-flex flex-column align-items-center justify-content-center py-5">
        <mat-spinner diameter="40"></mat-spinner>
        <p class="mt-3 text-muted">{{ 'general.waiting' | translate }}...</p>
    </div>

    <!-- Error State -->
    <div *ngIf="!isChecking && hasConnectionError" class="alert alert-danger text-center p-4">
        <mat-icon class="align-middle mr-1">error</mat-icon>
        <span class="align-middle font-weight-bold">
            Failed to connect to backend service. Please check your network connection and try again.
        </span>
        <div class="mt-3">
            <button mat-raised-button color="primary" (click)="checkStatus()">
                <mat-icon>refresh</mat-icon> Retry
            </button>
        </div>
    </div>

    <!-- Main Content -->
    <div *ngIf="!isChecking && !hasConnectionError" class="step-content">
        <!-- Standard Stepper Header (aligned left with action buttons) -->
        <div class="d-flex align-items-center justify-content-between mb-4">
            <div class="biometric-select-wrapper">
                <app-select [value]="selectedType" (selectChange)="onTypeChange($event)" class="biometric-type-select">
                    <app-option [value]="BIOMETRIC_TYPE.FINGERPRINT">{{ 'member.fingerprint' | translate }}</app-option>
                    <app-option [value]="BIOMETRIC_TYPE.FACE">{{ 'member.face_photo' | translate }}</app-option>
                </app-select>
            </div>

            <div class="action-buttons">
                <ng-container *ngIf="selectedType === BIOMETRIC_TYPE.FINGERPRINT">
                    <!-- Verify button visible if HAS_FINGERPRINT -->
                    <button *ngIf="state === FINGERPRINT_STATE.HAS_FINGERPRINT" type="button" mat-raised-button color="primary"
                            (click)="verifyFingerprint()" [disabled]="isFingerprintVerifying" class="m-button-primary approve s-md">
                        <mat-spinner *ngIf="isFingerprintVerifying" diameter="18" class="mr-1 d-inline-block"></mat-spinner>
                        <mat-icon *ngIf="!isFingerprintVerifying" class="mr-1" style="font-size: 18px; width: 18px; height: 18px;">done_all</mat-icon>
                        {{ 'fingerprint.verify' | translate }}
                    </button>

                    <!-- Register button visible if no fingerprint / not member -->
                    <button *ngIf="state === FINGERPRINT_STATE.NO_FINGERPRINT_MEMBER || state === FINGERPRINT_STATE.NOT_MEMBER" type="button" mat-raised-button
                            (click)="openRegister()" class="m-button-primary s-md">
                        <mat-icon class="mr-1" style="font-size: 18px; width: 18px; height: 18px;">fingerprint</mat-icon>
                        {{ 'fingerprint.register' | translate }}
                    </button>
                </ng-container>

                <ng-container *ngIf="selectedType === BIOMETRIC_TYPE.FACE">
                    <button *ngIf="hasPhoto && !isCheckingFace" type="button" mat-raised-button color="primary"
                            (click)="verifyFace()" [disabled]="!photoFile || isFaceVerifying" class="m-button-primary approve s-md">
                        <mat-spinner *ngIf="isFaceVerifying" diameter="18" class="mr-1 d-inline-block"></mat-spinner>
                        <mat-icon *ngIf="!isFaceVerifying" class="mr-1" style="font-size: 18px; width: 18px; height: 18px;">done_all</mat-icon>
                        {{ 'face.verify' | translate }}
                    </button>
                </ng-container>
            </div>
        </div>

        <!-- Fingerprint View -->
        <ng-container *ngIf="selectedType === BIOMETRIC_TYPE.FINGERPRINT">
            <div class="d-flex flex-column align-items-center justify-content-center mt-4 my-0 py-0">
                <!-- Illustrations based on status -->
                <div class="mb-4">
                    <img *ngIf="verified" src="assets/icons/scan-verify-success.svg" style="width: 160px; height: 160px;" alt="verified">
                    <img *ngIf="!verified && state === FINGERPRINT_STATE.HAS_FINGERPRINT" src="assets/icons/fingerprint-success.svg" style="width: 160px; height: 160px;" alt="has fingerprint">
                    <img *ngIf="!verified && (state === FINGERPRINT_STATE.NO_FINGERPRINT_MEMBER || state === FINGERPRINT_STATE.NOT_MEMBER)" src="assets/icons/fingerprint-search.svg" style="width: 160px; height: 160px;" alt="no fingerprint">
                </div>

                <p class="fingerprint-status-msg mt-2 text-center">
                    <ng-container *ngIf="verified">
                        {{ 'fingerprint.verify_match' | translate }}
                    </ng-container>
                    <ng-container *ngIf="!verified && state === FINGERPRINT_STATE.HAS_FINGERPRINT">
                        {{ 'fingerprint.verify_dialog_msg' | translate }}
                    </ng-container>
                    <ng-container *ngIf="!verified && state === FINGERPRINT_STATE.NO_FINGERPRINT_MEMBER">
                        {{ 'fingerprint.no_fingerprint' | translate }}
                    </ng-container>
                    <ng-container *ngIf="!verified && state === FINGERPRINT_STATE.NOT_MEMBER">
                        {{ 'fingerprint.not_member' | translate }}
                    </ng-container>
                </p>

                <!-- Skip Scan Checkbox centered below status -->
                <div class="mt-5">
                    <mat-checkbox [(ngModel)]="isSkipped" (change)="onSkipChange($event)">
                        {{ 'fingerprint.skip_scan' | translate }}
                    </mat-checkbox>
                </div>
            </div>
        </ng-container>

        <!-- Face Capture View -->
        <ng-container *ngIf="selectedType === BIOMETRIC_TYPE.FACE">
            <!-- Face Loading State -->
            <div *ngIf="isCheckingFace" class="d-flex flex-column align-items-center justify-content-center py-5">
                <mat-spinner diameter="40"></mat-spinner>
                <p class="mt-3 text-muted">{{ 'general.waiting' | translate }}...</p>
            </div>

            <!-- Case 1: Member has face photo for verification -->
            <div *ngIf="!isCheckingFace && hasPhoto" class="d-flex align-items-center face-wrapper flex-column mt-4">
                <app-file-drag-drop [useInputFile]="false" (labelClick)="onOpenCamera()" (fileChange)="onFileChange($event)">
                    <img width="100" height="100" [src]="photoSource || 'assets/icons/user.svg'" class="avatar-preview">
                </app-file-drag-drop>
                <h3 class="note my-3 text-center">
                    <span class="text-danger font-weight-bold">{{'member.search_face_.notice' | translate}}៖ </span>
                    <span>{{'member.search_face_.warn' | translate}}</span>
                </h3>

                <!-- Skip Face Verification Checkbox -->
                <div class="mt-4">
                    <mat-checkbox [(ngModel)]="isSkipped" (change)="onSkipChange($event)">
                        {{ 'face.skip_verify' | translate }}
                    </mat-checkbox>
                </div>
            </div>

            <!-- Case 2: Member does NOT have face photo -->
            <div *ngIf="!isCheckingFace && !hasPhoto" class="d-flex flex-column align-items-center justify-content-center mt-4 my-0 py-0">
                <div class="mb-4">
                    <img src="assets/icons/empty_list.svg" style="width: 160px; height: 160px;" alt="no face photo">
                </div>

                <p class="fingerprint-status-msg mt-2 text-center">
                    {{ 'face.no_face' | translate }}
                </p>

                <!-- Skip Face Verification Checkbox -->
                <div class="mt-5">
                    <mat-checkbox [(ngModel)]="isSkipped" (change)="onSkipChange($event)">
                        {{ 'face.skip_verify' | translate }}
                    </mat-checkbox>
                </div>
            </div>
        </ng-container>
    </div>
</div>

